import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { ArrowLeftIcon, Loader2Icon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive font-mono text-xs lg:text-sm hover:cursor-pointer disabled:hover:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          'bg-linear-to-r/srgb from-[#0211D9] to-[#2736F8] border border-[#0211D9] text-primary-foreground shadow-xs hover:brightness-110',
        success: 'bg-success text-white shadow-xs hover:bg-success/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-primary shadow-xs hover:bg-accent/50 text-primary hover:text-primary dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/50',
        ghost: 'hover:bg-accent/50 text-primary px-2',
        link: 'text-primary underline-offset-4 hover:underline p-0 font-normal',
        linkMuted: 'text-muted-foreground underline-offset-4 hover:underline p-0 font-normal',
        muted: 'bg-muted shadow-xs hover:bg-muted/90 text-primary',
        background:
          'bg-background text-primary border border-border hover:bg-muted hover:text-primary',
      },
      size: {
        // ponytail: h-11 on mobile (~44px comfortable tap target), reset to h-9 at sm so desktop is unchanged.
        // Root fix for all default/lg CTAs being 36px on mobile. Ceiling: a caller overriding height with a
        // bare `h-*` (no sm: variant) would have this sm:h-9 leak onto desktop — audited, only newsletter-form
        // does that and it guards with sm:h-12. Add sm:h-<n> to any future base-height override.
        default: 'h-11 sm:h-9 has-[>svg]:px-4 py-5 px-5',
        sm: 'h-7 rounded-lg gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-11 sm:h-9 rounded-lg px-6 has-[>svg]:px-4',
        xl: 'h-12 rounded-lg px-6 has-[>svg]:px-4 py-8',
        icon: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

function ButtonLoading({
  loading,
  children,
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & {
  children: React.ReactNode
  loading: boolean
  className?: string
  variant?: VariantProps<typeof buttonVariants>['variant']
  size?: VariantProps<typeof buttonVariants>['size']
  asChild?: boolean
}) {
  return (
    <Button
      disabled={loading || props.disabled}
      className={cn(className)}
      variant={variant}
      size={size}
      asChild={asChild}
      {...props}
    >
      {loading && <Loader2Icon className="animate-spin" />}
      {loading && size === 'icon' ? null : children}
    </Button>
  )
}

function BackButton({
  variant = 'outline',
  size = 'icon',
  asChild = false,
  href,
  onClick,
  ...props
}: React.ComponentProps<'button'> & {
  variant?: VariantProps<typeof buttonVariants>['variant']
  size?: VariantProps<typeof buttonVariants>['size']
  asChild?: boolean
  href?: string
  onClick?: () => void
}) {
  return (
    <Button variant={variant} size={size} asChild={asChild} {...props}>
      <a href={href || ''} onClick={onClick}>
        <ArrowLeftIcon className="size-4" />
      </a>
    </Button>
  )
}


export { BackButton, Button, ButtonLoading, buttonVariants }
